/* ============================================================
   Lagomlo — ai-studio.js (Shopify theme)
   AI Studio page controller (port of preview/js/ai-studio.js):
   credits check, text/image generation, results gallery,
   "use this design" bridge into the product design studio, and
   the contest entry bridge.

   Session: no Auth module in the theme — the ai-studio.liquid section
   renders the customer context into data attributes:
     #aiStudio[data-ai-customer-email]   -> logged-in customer email ('' = guest)
     #aiStudio[data-ai-credits]          -> credit balance (metafield
        customer.metafields.lagomlo.credits_balance, falls back to the
        section's free_credits_on_signup, default 10)
   The section also embeds an i18n JSON blob (#aiStudioI18n) with the
   dynamic strings, so all copy stays in the locale file.

   Credits are DEMO ONLY: the frontend mirrors the balance in localStorage
   ("lagomlo.creditsBalance") so the flow is interactive without a backend.
   A Shopify App / backend MUST enforce real spending (and persist the
   balance to the customer metafield) before going live.

   Exposed: window.AiStudio
   ============================================================ */
(function (global) {
  'use strict';

  var AI_LS = 'lagomlo.aiHistory';
  var DESIGN_BRIDGE = 'lagomlo.aiDesign';
  var CREDITS_LS = 'lagomlo.creditsBalance';

  function el(id) { return document.getElementById(id); }

  function readLS(key, fallback) {
    try {
      var raw = global.localStorage.getItem(key);
      return raw === null ? fallback : JSON.parse(raw);
    } catch (e) { return fallback; }
  }
  function writeLS(key, value) {
    try { global.localStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
  }

  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ---------- Toast (tiny dependency-free replacement for Store.toast) ---------- */
  function toast(message) {
    var box = document.querySelector('.lagomlo-toasts');
    if (!box) {
      box = document.createElement('div');
      box.className = 'lagomlo-toasts';
      document.body.appendChild(box);
    }
    var t = document.createElement('div');
    t.className = 'lagomlo-toast';
    t.textContent = message;
    box.appendChild(t);
    setTimeout(function () {
      t.classList.add('is-leaving');
      setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 300);
    }, 2600);
  }

  /* ---------- i18n (strings rendered by Liquid into #aiStudioI18n) ---------- */
  function i18n() {
    var node = el('aiStudioI18n');
    if (!node) return {};
    try { return JSON.parse(node.textContent) || {}; } catch (e) { return {}; }
  }

  function fmtPlural(one, other, n) {
    return n === 1 ? one : other;
  }

  /* ---------- Session ---------- */
  function rootEl() { return el('aiStudio'); }

  function currentUser() {
    var root = rootEl();
    if (!root) return null;
    var email = (root.getAttribute('data-ai-customer-email') || '').trim();
    if (!email) return null;
    return { email: email, name: email.split('@')[0] || email };
  }

  /* ---------- Credits (demo mirror of customer.metafields.lagomlo.credits_balance) ---------- */
  function creditBalance() {
    var root = rootEl();
    var base = root ? parseInt(root.getAttribute('data-ai-credits'), 10) : 0;
    if (isNaN(base)) base = 0;
    var local = readLS(CREDITS_LS, null);
    if (local === null || typeof local !== 'number' || isNaN(local)) {
      writeLS(CREDITS_LS, base); // seed the demo mirror once
      return base;
    }
    return local;
  }

  function setCreditBalance(n) {
    writeLS(CREDITS_LS, Math.max(0, parseInt(n, 10) || 0));
  }

  function spendCredit(reason) {
    var u = currentUser();
    if (!u) return { ok: false, reason: 'login' };
    var bal = creditBalance();
    if (bal <= 0) return { ok: false, reason: 'credits' };
    setCreditBalance(bal - 1);
    return { ok: true };
  }

  function addCredit(n, reason) {
    setCreditBalance(creditBalance() + (parseInt(n, 10) || 0));
  }

  /* ---------- Credits UI ---------- */
  function renderCredits() {
    var chip = el('aiCreditsChip');
    var txt = el('aiCreditsText');
    var t = i18n();
    var u = currentUser();
    if (!u) {
      if (txt) txt.textContent = t.credits_sign_in || 'Sign in to start creating';
      return;
    }
    var bal = creditBalance();
    if (txt) {
      txt.textContent = fmtPlural(t.credits_left_one, t.credits_left_other, bal)
        .replace('{{ count }}', String(bal));
    }
    if (chip) chip.classList.toggle('is-empty', bal <= 0);
  }

  /* ---------- Mode tabs ---------- */
  function bindModeTabs() {
    var tabs = document.querySelectorAll('.ai-mode-tab');
    if (!tabs.length) return;
    Array.prototype.forEach.call(tabs, function (tab) {
      tab.addEventListener('click', function () {
        var mode = tab.getAttribute('data-ai-mode');
        Array.prototype.forEach.call(tabs, function (t) {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });
        var textPanel = el('aiPanelText');
        var imgPanel = el('aiPanelImage');
        if (textPanel) textPanel.hidden = mode !== 'text';
        if (imgPanel) imgPanel.hidden = mode !== 'image';
      });
    });
  }

  /* ---------- Style chips ---------- */
  function bindStyleChips(rootId) {
    var root = el(rootId);
    if (!root) return;
    root.addEventListener('click', function (e) {
      var chip = e.target.closest ? e.target.closest('.ai-style-chip') : null;
      if (!chip) return;
      Array.prototype.forEach.call(root.querySelectorAll('.ai-style-chip'), function (c) { c.classList.remove('is-active'); });
      chip.classList.add('is-active');
    });
  }

  function activeStyle(rootId) {
    var root = el(rootId);
    if (!root) return '';
    var active = root.querySelector('.ai-style-chip.is-active');
    return active ? active.getAttribute('data-style') : '';
  }

  /* ---------- Generation ---------- */
  function setGenerating(on, text) {
    var g = el('aiGenerating');
    if (g) g.hidden = !on;
    if (text && el('aiGeneratingText')) el('aiGeneratingText').textContent = text;
  }

  function showEmpty(id) {
    ['aiNoCredits', 'aiLoginPrompt'].forEach(function (eid) {
      var e = el(eid);
      if (e) e.hidden = eid !== id;
    });
    var results = el('aiResults');
    if (results) results.hidden = true;
  }

  function guardBeforeGenerate() {
    var u = currentUser();
    if (!u) { showEmpty('aiLoginPrompt'); renderCredits(); return null; }
    var bal = creditBalance();
    if (bal <= 0) { showEmpty('aiNoCredits'); renderCredits(); return null; }
    return u;
  }

  function renderResults(images, prompt, style, meta) {
    var results = el('aiResults');
    var grid = el('aiResultsGrid');
    var t = i18n();
    if (!results || !grid) return;
    results.hidden = false;
    ['aiNoCredits', 'aiLoginPrompt'].forEach(function (eid) { var e = el(eid); if (e) e.hidden = true; });

    grid.innerHTML = '';
    images.forEach(function (dataUrl, i) {
      var card = document.createElement('div');
      card.className = 'ai-result-card';
      card.innerHTML =
        '<div class="ai-result-media">' +
        '  <img src="' + dataUrl + '" alt="' + escHtml((t.result_alt || 'AI design {{ count }}').replace('{{ count }}', String(i + 1))) + '" loading="lazy">' +
        '</div>' +
        '<div class="ai-result-actions">' +
        '  <button type="button" class="btn btn-primary btn-sm" data-use="' + i + '">' + escHtml(t.result_make || 'Make this a blanket') + '</button>' +
        '  <button type="button" class="btn btn-outline btn-sm" data-contest="' + i + '">' + escHtml(t.result_contest || 'Enter contest') + '</button>' +
        '</div>';
      grid.appendChild(card);
    });

    // actions
    Array.prototype.forEach.call(grid.querySelectorAll('[data-use]'), function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.getAttribute('data-use'), 10);
        useDesign(images[idx], prompt, style);
      });
    });
    Array.prototype.forEach.call(grid.querySelectorAll('[data-contest]'), function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.getAttribute('data-contest'), 10);
        enterContest(images[idx], prompt, style);
      });
    });

    // record history (demo only — localStorage; a real backend should own this)
    var u = currentUser();
    if (u) {
      var list = readLS(AI_LS, []);
      list.unshift({ id: 'ai_' + Date.now().toString(36), user: u.email, prompt: prompt, style: style || '', imageDataUrl: images[0], createdAt: new Date().toISOString(), usedInDesign: false });
      list = list.slice(0, 50);
      writeLS(AI_LS, list);
    }
  }

  function doGenerate(prompt, style, refDataUrl) {
    var guard = guardBeforeGenerate();
    if (!guard) return;
    var t = i18n();
    setGenerating(true, refDataUrl ? (t.generating_image || 'Remixing your photo\u2026') : (t.generating_text || 'Dreaming up your design\u2026'));

    // spend the credit up front; refund on failure
    var spent = spendCredit(refDataUrl ? 'AI image-to-image' : 'AI text-to-image');
    if (!spent.ok) { setGenerating(false); showEmpty(spent.reason === 'login' ? 'aiLoginPrompt' : 'aiNoCredits'); renderCredits(); return; }

    var fullPrompt = style ? prompt + ', ' + style + ' style' : prompt;
    AIProvider.generate(fullPrompt, { style: style, refImage: refDataUrl })
      .then(function (res) {
        setGenerating(false);
        renderResults(res.images, prompt, style, res.meta);
        renderCredits();
      })
      .catch(function (err) {
        // refund the credit on failure
        var u = currentUser();
        if (u) addCredit(1, 'refund');
        setGenerating(false);
        renderCredits();
        toast((t.toast_generation_failed || 'Generation failed: {error}').replace('{error}', err && err.message || ''));
      });
  }

  /* ---------- Reference image (image-to-image) ---------- */
  function bindRefUpload() {
    var drop = el('aiRefDrop');
    var input = el('aiRefInput');
    var preview = el('aiRefPreview');
    var img = el('aiRefImg');
    var removeBtn = el('aiRefRemove');
    var refData = { url: null };

    function setRef(dataUrl) {
      refData.url = dataUrl;
      if (img) img.src = dataUrl;
      if (drop) drop.hidden = !!dataUrl;
      if (preview) preview.hidden = !dataUrl;
    }

    function readFile(file) {
      var reader = new FileReader();
      reader.onload = function () { setRef(reader.result); };
      reader.readAsDataURL(file);
    }

    if (drop) {
      drop.addEventListener('click', function () { if (input) input.click(); });
      drop.addEventListener('dragover', function (e) { e.preventDefault(); drop.classList.add('is-drag'); });
      drop.addEventListener('dragleave', function () { drop.classList.remove('is-drag'); });
      drop.addEventListener('drop', function (e) {
        e.preventDefault();
        drop.classList.remove('is-drag');
        var f = e.dataTransfer.files && e.dataTransfer.files[0];
        if (f && f.type.indexOf('image/') === 0) readFile(f);
      });
    }
    if (input) input.addEventListener('change', function () {
      var f = input.files && input.files[0];
      if (f) readFile(f);
    });
    if (removeBtn) removeBtn.addEventListener('click', function () { setRef(null); if (input) input.value = ''; });

    return function () { return refData.url; };
  }

  /* ---------- Bridge to design studio / contest ---------- */
  function productAiUrl() {
    var routes = (global.Lagomlo && global.Lagomlo.routes) || {};
    return (routes.root || '/') + 'products/photo-blanket?ai=1';
  }

  function contestsUrl() {
    var routes = (global.Lagomlo && global.Lagomlo.routes) || {};
    return (routes.root || '/') + 'pages/contests';
  }

  function useDesign(dataUrl, prompt, style) {
    var t = i18n();
    writeLS(DESIGN_BRIDGE, {
      imageDataUrl: dataUrl,
      prompt: prompt || '',
      style: style || '',
      from: 'ai-studio'
    });
    // mark history used
    var list = readLS(AI_LS, []);
    if (list.length && list[0].imageDataUrl === dataUrl) { list[0].usedInDesign = true; writeLS(AI_LS, list); }
    toast(t.toast_design_loaded || 'Design loaded \u2014 customize it in the studio!');
    window.location.href = productAiUrl();
  }

  function enterContest(dataUrl, prompt, style) {
    var t = i18n();
    writeLS(DESIGN_BRIDGE, {
      imageDataUrl: dataUrl,
      prompt: prompt || '',
      style: style || '',
      from: 'ai-studio'
    });
    toast(t.toast_design_saved || 'Design saved \u2014 pick a contest to enter it in!');
    window.location.href = contestsUrl() + '?enter=1';
  }

  /* ---------- Init ---------- */
  function init() {
    if (!el('aiStudio') && !el('aiPromptText')) return; // not on AI Studio page
    renderCredits();
    bindModeTabs();
    bindStyleChips('aiStyleChipsText');
    bindStyleChips('aiStyleChipsImage');
    var getRef = bindRefUpload();
    var t = i18n();

    var formText = el('aiFormText');
    if (formText) formText.addEventListener('submit', function (e) {
      e.preventDefault();
      var prompt = (el('aiPromptText').value || '').trim();
      if (!prompt) { toast(t.toast_describe || 'Please describe your design first.'); return; }
      doGenerate(prompt, activeStyle('aiStyleChipsText'), null);
    });

    var formImage = el('aiFormImage');
    if (formImage) formImage.addEventListener('submit', function (e) {
      e.preventDefault();
      var prompt = (el('aiPromptImage').value || '').trim();
      var ref = getRef();
      if (!ref) { toast(t.toast_upload || 'Please upload a reference photo first.'); return; }
      if (!prompt) { toast(t.toast_transform || 'Describe how to transform your photo.'); return; }
      doGenerate(prompt, activeStyle('aiStyleChipsImage'), ref);
    });

    // If we arrived with a pending design (from contest "enter"), keep it in storage.
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get('enter') === '1') {
        toast(t.entry_hint || 'Now pick or create a contest to enter your design in.');
      }
    } catch (err) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  global.AiStudio = { init: init, useDesign: useDesign, enterContest: enterContest };
})(window);
